"use client"

import * as React from "react"
import { Check, ChevronDown } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@comps/lib/utils"
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@comps/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@comps/ui/popover"
import { selectTriggerClassName } from "@comps/ui/select"
import { matchesSearch } from "@utils/search"

export type SearchableSelectOption = {
  value: string
  label: string
  disabled?: boolean
}

type SearchableSelectProps = {
  options: SearchableSelectOption[]
  value?: string | null
  onValueChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  // Applied to the trigger, as on a SelectTrigger.
  className?: string
}

// SearchableSelect looks like a Select when closed; once opened, a search
// input on top of the list filters the options (see matchesSearch).
export function SearchableSelect({ options, value, onValueChange, placeholder, disabled, className }: SearchableSelectProps) {
  const { t } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")
  const contentId = React.useId()

  const selected = options.find(option => option.value === value)
  const visibleOptions = React.useMemo(
    () => options.filter(option => matchesSearch(option.label, search)),
    [options, search]
  )

  function onOpenChange(nextOpen: boolean) {
    setOpen(nextOpen)
    if (!nextOpen) {
      setSearch("")
    }
  }

  function onOptionSelect(option: SearchableSelectOption) {
    onOpenChange(false)
    onValueChange(option.value)
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button type="button" role="combobox" aria-haspopup="listbox" aria-expanded={open} aria-controls={contentId} disabled={disabled} className={cn(selectTriggerClassName, className)}>
          <span>{selected?.label ?? placeholder}</span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        id={contentId}
        role="presentation"
        side="bottom"
        className="flex w-auto min-w-[var(--radix-popover-trigger-width)] max-h-[var(--radix-popover-content-available-height)] flex-col overflow-hidden p-0">
        {/* Items are identified by their value: two columns may share a label. */}
        <Command shouldFilter={false} className="min-h-0">
          <CommandInput value={search} onValueChange={setSearch} placeholder={t("field.search.placeholder")} autoFocus={true} />
          <CommandList className="max-h-72 min-h-0 p-1 scrollbar-thin scrollbar-button-hidden">
            <CommandEmpty>{t("field.search.noResults")}</CommandEmpty>
            {visibleOptions.map(option => (
              <CommandItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                onSelect={() => onOptionSelect(option)}
                className="cursor-pointer pr-8">
                <span className="truncate">{option.label}</span>
                {option.value === value && <Check className="absolute right-2" />}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
